import { Navigate } from "@tanstack/react-router";
import { ROUTES } from "@/constants/routeConstants";
import { useSignupConfig } from "@/features/account-nux/hooks/useSignupConfig";
import { CreateOrganizationView } from "@/features/onboarding/create-organization-view";
import { OrganizationLoadError } from "@/features/onboarding/organization-load-error";
import { PendingApprovalView } from "@/features/onboarding/pending-approval-view";
import { SignupConfigLoadError } from "@/features/onboarding/signup-config-load-error";
import { useAuthStore } from "@/store/auth";
import { useOrganizationStore } from "@/store/organization";

/**
 * Routes authenticated onboarding from organization and signup-config state.
 * Organization and signup-config failures render recoverable fallbacks so an
 * outage cannot be mistaken for an unmetered signup. Eligible new users and
 * enrolled pending admins replace-navigate to NUX; sales-led new users create an
 * organization, other pending users see the approval view, and active users
 * replace-navigate home. Persisted enrollment keeps an existing signup resumable
 * when flag evaluation is unavailable.
 *
 * @returns The selected onboarding view, fallback, redirect, or `null` while loading.
 */
export const Onboarding = () => {
  const user = useAuthStore((s) => s.user);
  const organization = useOrganizationStore((s) => s.organization);
  const isLoading = useOrganizationStore((s) => s.isLoading);
  const isSwitching = useOrganizationStore((s) => s.isSwitching);
  const organizationError = useOrganizationStore((s) => s.error);
  const {
    data: signupConfig,
    isError: isConfigError,
    isFetching: isConfigFetching,
  } = useSignupConfig();

  const isPendingApproval = organization?.account_status === "pending_approval";
  const isProvisioning = organization?.account_status === "provisioning";

  if (user?.requires_terms_acceptance === true) {
    return <Navigate to={ROUTES.ACCOUNT_NUX} replace={true} />;
  }

  if (!organization && organizationError) {
    return <OrganizationLoadError />;
  }

  if (isLoading || (!organization && isConfigFetching)) {
    return null;
  }

  if (!organization && isConfigError) {
    return <SignupConfigLoadError />;
  }

  const selfServeEnabled =
    !isConfigError && signupConfig?.self_serve_signup_enabled === true;
  const isSelfServeResume =
    (isPendingApproval || isProvisioning) &&
    organization?.self_serve_billing_enabled === true &&
    organization?.role === "admin";

  if ((selfServeEnabled && !organization) || isSelfServeResume) {
    return <Navigate to={ROUTES.ACCOUNT_NUX} replace={true} />;
  }

  if (!organization) {
    return <CreateOrganizationView />;
  }

  if (isSwitching && organization.account_status === "active") {
    return <CreateOrganizationView />;
  }

  // If user has an organization and it's pending approval, show pending view
  if (isPendingApproval || isProvisioning) {
    return <PendingApprovalView />;
  }

  return <Navigate to={ROUTES.HOME} replace={true} />;
};
